import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { toast } from "sonner";
import { useLang } from "../../context/LangContext";
import { usePrivacy } from "../../context/PrivacyContext";
import { c } from "../../content";
import { SITE } from "../../site";

const BORDER_IDLE  = "rgba(255,255,255,0.10)";
const BORDER_FOCUS = "#C87941";
const BORDER_ERROR = "rgba(248,113,113,0.70)";

const lineCls =
  "w-full bg-transparent border-0 border-b pb-3 pt-2 font-body text-sm text-white-warm outline-none placeholder:text-white/20 transition-colors duration-200";

function Label({ children }) {
  return (
    <label className="font-body text-[10px] tracking-[0.25em] uppercase block mb-2" style={{ color: "rgba(242,232,216,0.32)" }}>
      {children}
    </label>
  );
}

function FieldError({ msg }) {
  return (
    <AnimatePresence initial={false}>
      {msg && (
        <motion.p
          key={msg}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="font-mono text-[10px] tracking-[0.12em] mt-1.5"
          style={{ color: "rgba(248,113,113,0.90)" }}
        >
          {msg}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const encode = (data) =>
  Object.keys(data).map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`).join("&");

const selectArrow =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='rgba(242,232,216,0.35)' stroke-width='1.5' fill='none'/></svg>\")";

export default function Contact() {
  const { lang } = useLang();
  const { openPolicy } = usePrivacy();
  const [state, setState] = useState({ name: "", email: "", type: "", budget: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const projectTypes = c.contact.project_types[lang];

  const validate = (s) => {
    const errs = {};
    if (!s.name.trim())        errs.name    = c.contact.errors.name[lang];
    if (!s.email.trim())       errs.email   = c.contact.errors.email_req[lang];
    else if (!EMAIL_RE.test(s.email)) errs.email = c.contact.errors.email_inv[lang];
    if (!s.message.trim())     errs.message = c.contact.errors.message[lang];
    return errs;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    const errs = validate(state);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStatus("loading");
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", "bot-field": "", ...state }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
      toast.success(c.contact.toast_ok[lang]);
      setState({ name: "", email: "", type: "", budget: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch {
      setStatus("idle");
      toast.error(c.contact.toast_err[lang]);
    }
  };

  return (
    <section id="kontakt" className="section-pad bg-navy-3 relative overflow-hidden" data-testid="contact-section">
      <div className="pointer-events-none absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(200,121,65,0.05), rgba(200,121,65,0) 70%)", filter: "blur(60px)" }} />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.45fr] gap-16 lg:gap-28 items-start relative">

        {/* ── LEFT ── */}
        <div className="reveal">
          <SectionLabel>{c.contact.label[lang]}</SectionLabel>

          <SplitHeading
            line1={c.contact.heading1[lang]}
            line2={<em className="italic text-amber">{c.contact.ice[lang]}</em>}
            className="font-display font-light leading-[1.04] mt-5 text-white-warm"
            style={{ fontSize: "clamp(2.4rem, 4.8vw, 3.8rem)" }}
            data-testid="contact-title"
          />

          <p className="font-body text-base leading-[1.85] mt-7 max-w-sm" style={{ color: "rgba(242,232,216,0.55)" }}>
            {c.contact.subtitle[lang]}
          </p>

          <div className="my-9 h-px" style={{ width: 48, background: "rgba(255,255,255,0.14)" }} />

          <p className="font-body text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: "rgba(242,232,216,0.32)" }}>
            {c.contact.email_label[lang]}
          </p>
          <a
            href={`mailto:${SITE.email}`}
            className="font-display font-light leading-tight text-white-warm hover:text-amber transition-colors duration-300"
            style={{ fontSize: "clamp(1rem, 3.5vw, 1.75rem)", display: "block", wordBreak: "break-all" }}
            data-testid="contact-email"
          >
            {SITE.email}
          </a>

          <p className="font-body text-[10px] tracking-[0.28em] uppercase mb-3 mt-8" style={{ color: "rgba(242,232,216,0.32)" }}>
            {c.contact.phone_label[lang]}
          </p>
          <a
            href={`tel:${SITE.phone}`}
            className="font-display font-light leading-tight text-white-warm hover:text-amber transition-colors duration-300"
            style={{ fontSize: "clamp(1rem, 3.5vw, 1.75rem)", display: "block" }}
            data-testid="contact-phone"
          >
            {SITE.phoneDisplay}
          </a>

          <ul className="mt-10 space-y-3.5">
            {c.contact.bullets[lang].map((b, i) => (
              <li key={i} className="flex items-center gap-3 font-body text-sm" style={{ color: "rgba(242,232,216,0.55)" }} data-testid={`contact-badge-${i}`}>
                <Check size={12} strokeWidth={2.5} className="shrink-0" style={{ color: "rgba(242,232,216,0.45)", opacity: 0.8 }} />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* ── RIGHT — form ── */}
        <div className="reveal d2 relative">
          {status === "success" && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center z-20"
              style={{ background: "rgba(16,15,12,0.85)", backdropFilter: "blur(6px)" }}
              data-testid="contact-success"
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" className="mb-6">
                <motion.circle cx="28" cy="28" r="24" stroke="rgba(124,196,216,0.45)" strokeWidth="1.5" fill="none" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }} />
                <motion.path d="M16 28 L23 35 L40 18" stroke="#7CC4D8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} />
              </svg>
              <h3 className="font-display text-2xl text-white-warm">{c.contact.success_title[lang]}</h3>
              <p className="font-body text-sm mt-2" style={{ color: "rgba(242,232,216,0.5)" }}>{c.contact.success_sub[lang]}</p>
            </div>
          )}

          <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-10" name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field">
            <input type="hidden" name="form-name" value="contact" />
            <input name="bot-field" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              <div>
                <Label>{c.contact.form.name_label[lang]}</Label>
                <input
                  name="name" value={state.name} onChange={onChange}
                  placeholder={c.contact.form.name_ph[lang]}
                  className={lineCls}
                  style={{ borderColor: errors.name ? BORDER_ERROR : BORDER_IDLE }}
                  onFocus={(e) => { e.target.style.borderColor = errors.name ? BORDER_ERROR : BORDER_FOCUS; }}
                  onBlur={(e)  => { e.target.style.borderColor = errors.name ? BORDER_ERROR : BORDER_IDLE;  }}
                  data-testid="contact-input-name"
                />
                <FieldError msg={errors.name} />
              </div>
              <div>
                <Label>{c.contact.form.email_label[lang]}</Label>
                <input
                  name="email" type="email" value={state.email} onChange={onChange}
                  placeholder={c.contact.form.email_ph[lang]}
                  className={lineCls}
                  style={{ borderColor: errors.email ? BORDER_ERROR : BORDER_IDLE }}
                  onFocus={(e) => { e.target.style.borderColor = errors.email ? BORDER_ERROR : BORDER_FOCUS; }}
                  onBlur={(e)  => { e.target.style.borderColor = errors.email ? BORDER_ERROR : BORDER_IDLE;  }}
                  data-testid="contact-input-email"
                />
                <FieldError msg={errors.email} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              <div>
                <Label>{c.contact.form.type_label[lang]}</Label>
                <select
                  name="type" value={state.type} onChange={onChange}
                  className={`${lineCls} appearance-none cursor-pointer`}
                  style={{ borderColor: BORDER_IDLE, backgroundImage: selectArrow, backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center", paddingRight: "1.2rem" }}
                  onFocus={(e) => { e.target.style.borderColor = BORDER_FOCUS; }}
                  onBlur={(e)  => { e.target.style.borderColor = BORDER_IDLE;  }}
                  data-testid="contact-input-type"
                >
                  <option value="" style={{ background: "#201A14" }}>{c.contact.form.type_ph[lang]}</option>
                  {projectTypes.map((t) => (
                    <option key={t} value={t} style={{ background: "#201A14" }}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{c.contact.form.budget_label[lang]}</Label>
                <select
                  name="budget" value={state.budget} onChange={onChange}
                  className={`${lineCls} appearance-none cursor-pointer`}
                  style={{ borderColor: BORDER_IDLE, backgroundImage: selectArrow, backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center", paddingRight: "1.2rem" }}
                  onFocus={(e) => { e.target.style.borderColor = BORDER_FOCUS; }}
                  onBlur={(e)  => { e.target.style.borderColor = BORDER_IDLE;  }}
                  data-testid="contact-input-budget"
                >
                  <option value="" style={{ background: "#201A14" }}>{c.contact.form.budget_ph[lang]}</option>
                  {c.contact.budgets[lang].map((b) => (
                    <option key={b} value={b} style={{ background: "#201A14" }}>{b}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>{c.contact.form.message_label[lang]}</Label>
              <textarea
                name="message" value={state.message} onChange={onChange} rows={5}
                placeholder={c.contact.form.message_ph[lang]}
                className="w-full bg-transparent font-body text-sm text-white-warm outline-none placeholder:text-white/20 resize-none p-4 transition-colors duration-200"
                style={{ border: `1px solid ${errors.message ? BORDER_ERROR : "rgba(255,255,255,0.08)"}`, borderRadius: 3 }}
                onFocus={(e) => { e.target.style.borderColor = errors.message ? BORDER_ERROR : BORDER_FOCUS; }}
                onBlur={(e)  => { e.target.style.borderColor = errors.message ? BORDER_ERROR : "rgba(255,255,255,0.08)"; }}
                data-testid="contact-input-message"
              />
              <FieldError msg={errors.message} />
            </div>

            <div>
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-primary w-full justify-center"
                style={{ opacity: status === "loading" ? 0.6 : 1 }}
                data-testid="contact-submit"
              >
                {status === "loading" ? c.contact.form.submitting[lang] : c.contact.form.submit[lang]}
              </button>
              <p className="font-body text-[10px] tracking-wide mt-4 text-center" style={{ color: "rgba(242,232,216,0.28)" }}>
                {c.contact.form.privacy[lang]}{" · "}
                <button
                  type="button"
                  onClick={openPolicy}
                  className="transition-colors duration-200 underline underline-offset-2"
                  style={{ color: "rgba(200,121,65,0.5)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#C87941"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(200,121,65,0.5)"; }}
                >
                  {c.contact.form.privacy_link[lang]}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
